import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Compass, MapPin, Moon, Navigation, Printer, Sun } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { Button } from "@/components/ui/button";
import { useAppStore } from "@/lib/store";
import { buildSeo } from "@/lib/seo";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { gregorianToBangla, formatBanglaDate } from "@/lib/bengaliDate";
import { BD_CITIES } from "@/data/bdCities";
import { INTERNATIONAL_CITIES } from "@/data/internationalCities";
import {
  computePrayerTimes,
  currentAndNext,
  formatTime,
  hijriString,
  METHODS,
  PRAYER_LABELS,
  type DailyTimes,
  type MadhabId,
  type MethodId,
  type PrayerKey,
} from "@/lib/calculators/prayerTimes";

type Bilingual = { en: string; bn: string };

const STR = {
  h1: { en: "Prayer Times (Bangladesh)", bn: "নামাজের সময়সূচী" },
  desc: {
    en: "Daily prayer times for all 64 districts of Bangladesh. Fajr, Dhuhr, Asr, Maghrib, Isha plus Sehri & Iftar — Karachi method, Hanafi madhhab.",
    bn: "বাংলাদেশের ৬৪ জেলার দৈনিক নামাজের সময়সূচী। ফজর, যোহর, আসর, মাগরিব, এশা — সেহরি ও ইফতারসহ। করাচী মেথড, হানাফী মাযহাব।",
  },
  location: { en: "Location", bn: "অবস্থান" },
  pickCity: { en: "Pick city", bn: "শহর নির্বাচন" },
  bdCities: { en: "Bangladesh — 64 districts", bn: "বাংলাদেশ — ৬৪ জেলা" },
  intlCities: { en: "International", bn: "আন্তর্জাতিক" },
  detect: { en: "Use my location", bn: "আমার অবস্থান" },
  manual: { en: "Manual coordinates", bn: "ম্যানুয়াল স্থানাঙ্ক" },
  method: { en: "Calculation method", bn: "গণনা পদ্ধতি" },
  madhhab: { en: "Madhhab (Asr)", bn: "মাযহাব (আসর)" },
  hanafi: { en: "Hanafi (2× shadow) — BD default", bn: "হানাফী (২× ছায়া) — ডিফল্ট" },
  standard: { en: "Standard (Shafi'i / Maliki / Hanbali)", bn: "শাফি'ঈ / মালিকী / হাম্বলী" },
  date: { en: "Date", bn: "তারিখ" },
  today: { en: "Today", bn: "আজ" },
  format: { en: "Time format", bn: "সময় বিন্যাস" },
  next: { en: "Next prayer", bn: "পরবর্তী নামাজ" },
  in: { en: "in", bn: "পরে" },
  ramadan: { en: "Sehri & Iftar", bn: "সেহরি ও ইফতার" },
  sehriEnds: { en: "Sehri ends", bn: "সেহরির শেষ সময়" },
  iftar: { en: "Iftar", bn: "ইফতারের সময়" },
  fastingHours: { en: "Today's fast", bn: "আজকের রোজা" },
  monthly: { en: "Monthly calendar", bn: "মাসিক ক্যালেন্ডার" },
  qibla: { en: "Qibla direction", bn: "কিবলার দিক" },
  print: { en: "Print", bn: "প্রিন্ট" },
};

const FAQ: { q: Bilingual; a: Bilingual }[] = [
  { q: { en: "How are prayer times calculated?", bn: "নামাজের সময় কীভাবে গণনা হয়?" }, a: { en: "From the sun's position relative to your latitude/longitude. Fajr and Isha use a sun-depression angle below the horizon, defined by each calculation method.", bn: "সূর্যের অবস্থান ও আপনার অক্ষাংশ-দ্রাঘিমার ভিত্তিতে। ফজর ও এশার জন্য সূর্যের দিগন্তের নিচে কোণ ব্যবহৃত হয় — প্রতিটি মেথডে আলাদা।" } },
  { q: { en: "Why is Hanafi Asr different?", bn: "হানাফী আসরের সময় কেন আলাদা?" }, a: { en: "Hanafi Asr begins when an object's shadow is twice its own length plus the noon shadow; other schools use one shadow length.", bn: "হানাফী মতে, কোনো বস্তুর ছায়া তার দ্বিগুণ + যোহরের ছায়ার সমান হলে আসর শুরু। অন্যান্য মাযহাবে এক ছায়া।" } },
  { q: { en: "When does Sehri end?", bn: "সেহরি কখন শেষ হয়?" }, a: { en: "Sehri ends at the start of Fajr. Many in Bangladesh stop eating ~3 minutes before Fajr as a precaution.", bn: "ফজর শুরুর সাথে সাথে সেহরি শেষ। বাংলাদেশে অনেকে সতর্কতা হিসেবে ফজরের ~৩ মিনিট আগে খাওয়া বন্ধ করেন।" } },
  { q: { en: "When is Iftar?", bn: "ইফতার কখন?" }, a: { en: "At Maghrib — the moment of sunset.", bn: "মাগরিবের সময় — সূর্যাস্তের মুহূর্তে।" } },
  { q: { en: "Best calculation method for Bangladesh?", bn: "বাংলাদেশের জন্য কোন method?" }, a: { en: "Karachi (University of Islamic Sciences). Most BD madrasas and Islamic Foundation schedules align with this method.", bn: "করাচী (University of Islamic Sciences)। বাংলাদেশের অধিকাংশ মাদ্রাসা ও ইসলামিক ফাউন্ডেশনের সময়সূচী এর সাথে মেলে।" } },
  { q: { en: "Why does my Asr differ from local mosque?", bn: "মসজিদের আসরের সাথে পার্থক্য কেন?" }, a: { en: "Mosques may add a precautionary buffer (5–10 min) before Iqamah, or use a slightly different method/madhhab.", bn: "মসজিদ সাধারণত ইক্বামার আগে ৫–১০ মিনিট সতর্কতা রাখে, বা সামান্য আলাদা মেথড/মাযহাব ব্যবহার করে।" } },
  { q: { en: "Prayer times for Dhaka?", bn: "ঢাকার নামাজের সময়?" }, a: { en: "Select Dhaka from the city list — defaults to Dhaka district headquarters (23.81°N, 90.41°E).", bn: "শহর তালিকা থেকে ঢাকা নির্বাচন করুন — ঢাকা জেলা সদর (২৩.৮১°N, ৯০.৪১°E) ডিফল্ট।" } },
  { q: { en: "Prayer times for Sylhet?", bn: "সিলেটের নামাজের সময়?" }, a: { en: "Select Sylhet — times are typically a few minutes earlier than Dhaka due to its eastern longitude.", bn: "সিলেট নির্বাচন করুন — পূর্ব দ্রাঘিমার কারণে ঢাকার চেয়ে কয়েক মিনিট আগে।" } },
  { q: { en: "Prayer times for Chittagong?", bn: "চট্টগ্রামের নামাজের সময়?" }, a: { en: "Select Chittagong — also slightly earlier than Dhaka.", bn: "চট্টগ্রাম নির্বাচন করুন — ঢাকার চেয়ে সামান্য আগে।" } },
  { q: { en: "Can I get prayer times for travel?", bn: "ভ্রমণে নামাজের সময়?" }, a: { en: "Yes — pick the destination city or use 'Use my location' upon arrival. Travelers (mosafir, 78km+) may shorten Dhuhr, Asr, Isha to 2 raka'at (qasr).", bn: "হ্যাঁ — গন্তব্য নির্বাচন করুন বা পৌঁছে 'আমার অবস্থান' ব্যবহার করুন। মুসাফির (৭৮কিমি+) যোহর/আসর/এশা ২ রাকাত (কসর) পড়তে পারেন।" } },
  { q: { en: "What about missed prayers (qaza)?", bn: "কাযা নামাজ?" }, a: { en: "A missed obligatory prayer must be made up (qaza) when remembered, before the next prayer if possible.", bn: "ছুটে যাওয়া ফরয নামাজ স্মরণ হলে কাযা পড়তে হবে — সম্ভব হলে পরের ওয়াক্তের আগে।" } },
  { q: { en: "How accurate is the Hijri date shown?", bn: "হিজরি তারিখ কতটা সঠিক?" }, a: { en: "We use the Umm al-Qura (Saudi astronomical) calendar. Local moon sighting in Bangladesh may shift Eid/Ramadan by ±1 day.", bn: "উম্মুল কুরা (সৌদি জ্যোতির্বৈজ্ঞানিক) ক্যালেন্ডার ব্যবহার করি। বাংলাদেশে স্থানীয় চাঁদ দেখার ভিত্তিতে ঈদ/রমাদান ±১ দিন আগ-পিছ হতে পারে।" } },
];

const STORAGE_KEY = "prayer-times-prefs-v1";

export const Route = createFileRoute("/prayer-times")({
  head: () => {
    const seo = buildSeo({
      title: "নামাজের সময়সূচী — Prayer Times Bangladesh Today",
      description:
        "বাংলাদেশের ৬৪ জেলার নামাজের সময়সূচী। ফজর, যোহর, আসর, মাগরিব, এশা। সেহরি ইফতারের সময়। Karachi method, Hanafi madhhab. Prayer times for all 64 districts of Bangladesh.",
      path: "/prayer-times",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Prayer Times", path: "/prayer-times" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: PrayerTimesPage,
});

interface LocationState {
  source: "city" | "geo" | "manual";
  cityKey?: string;
  cityLabel: { en: string; bn: string };
  lat: number;
  lon: number;
}

const DEFAULT_LOCATION: LocationState = {
  source: "city",
  cityKey: "dhaka",
  cityLabel: { en: "Dhaka", bn: "ঢাকা" },
  lat: 23.8103,
  lon: 90.4125,
};

function PrayerTimesPage() {
  const lang = useAppStore((s) => s.lang);
  const setLang = useAppStore((s) => s.setLang);
  const t = (k: Bilingual) => k[lang];

  // Force Bengali default if user hasn't picked
  useEffect(() => {
    if (!useAppStore.getState().langTouched) setLang("bn");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [location, setLocation] = useState<LocationState>(DEFAULT_LOCATION);
  const [method, setMethod] = useState<MethodId>("Karachi");
  const [madhab, setMadhab] = useState<MadhabId>("hanafi");
  const [date, setDate] = useState<Date>(new Date());
  const [timeFormat, setTimeFormat] = useState<"12" | "24">("12");
  const [now, setNow] = useState<Date>(new Date());
  const [manualLat, setManualLat] = useState("");
  const [manualLon, setManualLon] = useState("");
  const [showMonth, setShowMonth] = useState(false);

  // restore preferences
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d.location) setLocation(d.location);
      if (d.method) setMethod(d.method);
      if (d.madhab) setMadhab(d.madhab);
      if (d.timeFormat) setTimeFormat(d.timeFormat);
    } catch { /* noop */ }
  }, []);

  // persist
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ location, method, madhab, timeFormat }));
    } catch { /* noop */ }
  }, [location, method, madhab, timeFormat]);

  // 1-min ticker
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);

  const times: DailyTimes = useMemo(
    () => computePrayerTimes(location.lat, location.lon, date, method, madhab),
    [location.lat, location.lon, date, method, madhab],
  );

  const todayTimes: DailyTimes = useMemo(
    () => computePrayerTimes(location.lat, location.lon, now, method, madhab),
    [location.lat, location.lon, now, method, madhab],
  );

  const np = currentAndNext(todayTimes, now);

  const fmt = (d: Date) => {
    const s = formatTime(d, timeFormat);
    return lang === "bn" ? toBengaliNumerals(s) : s;
  };

  const onUseGeolocation = () => {
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({
          source: "geo",
          cityLabel: { en: "My location", bn: "আমার অবস্থান" },
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
        });
      },
      () => { /* permission denied -> stay on default */ },
      { timeout: 8000, enableHighAccuracy: false },
    );
  };

  const isRamadan = useMemo(() => {
    try {
      const month = new Intl.DateTimeFormat("en-u-ca-islamic-umalqura", { month: "numeric" }).format(now);
      return month === "9";
    } catch { return false; }
  }, [now]);

  const fastingDuration = useMemo(() => {
    const ms = todayTimes.maghrib.getTime() - todayTimes.fajr.getTime();
    const h = Math.floor(ms / 3_600_000);
    const m = Math.floor((ms % 3_600_000) / 60_000);
    return { h, m };
  }, [todayTimes]);

  const countdown = useMemo(() => {
    const ms = Math.max(0, np.nextTime.getTime() - now.getTime());
    const h = Math.floor(ms / 3_600_000);
    const m = Math.floor((ms % 3_600_000) / 60_000);
    return { h, m };
  }, [np.nextTime, now]);

  const banglaDate = formatBanglaDate(gregorianToBangla(date), lang);
  const hijri = hijriString(date, lang);
  const dateStr = date.toLocaleDateString(lang === "bn" ? "bn-BD" : "en-GB", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  const prayerRows: { key: PrayerKey; time: Date }[] = [
    { key: "fajr", time: times.fajr },
    { key: "sunrise", time: times.sunrise },
    { key: "dhuhr", time: times.dhuhr },
    { key: "asr", time: times.asr },
    { key: "maghrib", time: times.maghrib },
    { key: "isha", time: times.isha },
  ];

  const isToday = useMemo(() => {
    const a = date, b = new Date();
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }, [date]);

  return (
    <CalculatorLayout
      slug="prayer-times"
      title={t(STR.h1)}
      description={t(STR.desc)}
      iconName="Sunrise"
      faq={FAQ.map((f) => ({ q: t(f.q), a: t(f.a) }))}
    >
      <div className={lang === "bn" ? "font-bn space-y-6" : "space-y-6"}>
        {/* Location & method controls */}
        <div className="grid gap-4 md:grid-cols-2">
          <section className="space-y-2">
            <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t(STR.location)}
            </label>
            <select
              value={location.source === "city" ? location.cityKey : ""}
              onChange={(e) => {
                const v = e.target.value;
                const bd = BD_CITIES.find((c) => c.key === v);
                if (bd) {
                  setLocation({ source: "city", cityKey: bd.key, cityLabel: { en: bd.en, bn: bd.bn }, lat: bd.lat, lon: bd.lon });
                  return;
                }
                const intl = INTERNATIONAL_CITIES.find((c) => c.key === v);
                if (intl) {
                  setLocation({ source: "city", cityKey: intl.key, cityLabel: { en: intl.en, bn: intl.bn }, lat: intl.lat, lon: intl.lon });
                }
              }}
              className="w-full rounded-md border border-border bg-muted px-3 py-2 text-sm focus:border-accent focus:bg-background focus:outline-none focus:ring-1 focus:ring-accent/30"
            >
              <optgroup label={t(STR.bdCities)}>
                {BD_CITIES.map((c) => (
                  <option key={c.key} value={c.key}>{lang === "bn" ? c.bn : c.en} ({c.division})</option>
                ))}
              </optgroup>
              <optgroup label={t(STR.intlCities)}>
                {INTERNATIONAL_CITIES.map((c) => (
                  <option key={c.key} value={c.key}>{lang === "bn" ? c.bn : c.en}, {c.country}</option>
                ))}
              </optgroup>
            </select>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={onUseGeolocation}>
                <Navigation size={14} className="mr-1" />{t(STR.detect)}
              </Button>
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <MapPin size={12} /> {location.lat.toFixed(3)}, {location.lon.toFixed(3)}
              </span>
            </div>

            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground hover:text-accent">{t(STR.manual)}</summary>
              <div className="mt-2 flex gap-2">
                <input value={manualLat} onChange={(e) => setManualLat(e.target.value)} placeholder="Lat" className="w-24 rounded-md border border-border bg-muted px-2 py-1 text-xs" />
                <input value={manualLon} onChange={(e) => setManualLon(e.target.value)} placeholder="Lon" className="w-24 rounded-md border border-border bg-muted px-2 py-1 text-xs" />
                <Button size="sm" variant="outline" onClick={() => {
                  const la = Number(manualLat), lo = Number(manualLon);
                  if (Number.isFinite(la) && Number.isFinite(lo)) {
                    setLocation({ source: "manual", cityLabel: { en: "Custom", bn: "কাস্টম" }, lat: la, lon: lo });
                  }
                }}>OK</Button>
              </div>
            </details>
          </section>

          <section className="space-y-2">
            <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t(STR.method)}</label>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value as MethodId)}
              className="w-full rounded-md border border-border bg-muted px-3 py-2 text-sm focus:border-accent focus:bg-background focus:outline-none focus:ring-1 focus:ring-accent/30"
            >
              {METHODS.map((m) => (
                <option key={m.id} value={m.id}>{lang === "bn" ? m.bn : m.en}</option>
              ))}
            </select>

            <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t(STR.madhhab)}</label>
            <div className="flex flex-col gap-1.5">
              {([
                ["hanafi", t(STR.hanafi)],
                ["standard", t(STR.standard)],
              ] as [MadhabId, string][]).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setMadhab(id)}
                  className={`rounded-md border px-3 py-1.5 text-left text-xs ${
                    madhab === id ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground hover:border-accent/50"
                  }`}
                >{label}</button>
              ))}
            </div>
          </section>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <input
            type="date"
            value={date.toISOString().slice(0, 10)}
            onChange={(e) => { const d = new Date(e.target.value); if (!isNaN(d.getTime())) setDate(d); }}
            className="rounded-md border border-border bg-muted px-3 py-1.5 text-sm"
          />
          {!isToday && (
            <Button variant="outline" size="sm" onClick={() => setDate(new Date())}>{t(STR.today)}</Button>
          )}
          <div className="ml-auto flex gap-1 rounded-md border border-border bg-muted p-0.5 text-xs">
            {(["12", "24"] as const).map((f) => (
              <button key={f} onClick={() => setTimeFormat(f)}
                className={`rounded px-2 py-1 ${timeFormat === f ? "bg-card shadow-sm" : "text-muted-foreground"}`}>
                {f}h
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowMonth((s) => !s)}>{t(STR.monthly)}</Button>
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            <Printer size={14} className="mr-1" />{t(STR.print)}
          </Button>
        </div>

        {/* Ramadan card */}
        {isRamadan && (
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-emerald-400/40 bg-gradient-to-br from-emerald-50 to-teal-50 p-5 dark:border-emerald-700/50 dark:from-emerald-950/40 dark:to-teal-950/30"
          >
            <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800 dark:text-emerald-200">
              <Moon size={16} /> {t(STR.ramadan)}
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              <div>
                <p className="text-xs text-muted-foreground">{t(STR.sehriEnds)}</p>
                <p className="text-2xl font-bold tabular-nums">{fmt(todayTimes.fajr)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t(STR.iftar)}</p>
                <p className="text-2xl font-bold tabular-nums">{fmt(todayTimes.maghrib)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t(STR.fastingHours)}</p>
                <p className="text-2xl font-bold tabular-nums">
                  {lang === "bn" ? toBengaliNumerals(fastingDuration.h) : fastingDuration.h}{lang === "bn" ? " ঘণ্টা " : "h "}
                  {lang === "bn" ? toBengaliNumerals(fastingDuration.m) : fastingDuration.m}{lang === "bn" ? " মিনিট" : "m"}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Main prayer card */}
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border border-border bg-gradient-to-br from-card to-muted/30 p-5"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                {lang === "bn" ? "আজ" : "Today"} · {lang === "bn" ? location.cityLabel.bn : location.cityLabel.en}
              </p>
              <p className="text-lg font-semibold">{dateStr}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">{hijri} · {banglaDate}</p>
            </div>
            {isToday && np.next && (
              <div className="text-right">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{t(STR.next)}</p>
                <p className="text-lg font-bold">
                  {lang === "bn" ? PRAYER_LABELS[np.next].bn : PRAYER_LABELS[np.next].en}
                </p>
                <p className="text-sm tabular-nums text-accent">
                  {t(STR.in)} {lang === "bn" ? toBengaliNumerals(countdown.h) : countdown.h}h{" "}
                  {lang === "bn" ? toBengaliNumerals(countdown.m) : countdown.m}m
                </p>
              </div>
            )}
          </div>

          <ul className="mt-4 divide-y divide-border/60">
            {prayerRows.map(({ key, time }) => {
              const active = isToday && np.current === key;
              const isNext = isToday && np.next === key;
              return (
                <li key={key}
                  className={`flex items-center justify-between py-3 ${
                    active ? "rounded-lg bg-accent/10 px-3 -mx-3" : ""
                  }`}>
                  <span className={`flex items-center gap-2 text-sm ${active ? "font-semibold text-accent" : ""}`}>
                    {key === "sunrise" ? <Sun size={14} /> : <Moon size={14} className="opacity-60" />}
                    <span>{lang === "bn" ? PRAYER_LABELS[key].bn : PRAYER_LABELS[key].en}</span>
                    {lang === "bn" && <span className="text-xs text-muted-foreground">/ {PRAYER_LABELS[key].en}</span>}
                  </span>
                  <span className="flex items-center gap-2">
                    <span className={`text-base tabular-nums ${active ? "font-bold text-accent" : ""}`}>{fmt(time)}</span>
                    {isNext && <span className="rounded-full bg-accent/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-accent">{lang === "bn" ? "পরবর্তী" : "Next"}</span>}
                  </span>
                </li>
              );
            })}
          </ul>

          {/* Day progress bar */}
          {isToday && (
            <div className="mt-4">
              <div className="relative h-2 overflow-hidden rounded-full bg-muted">
                {(() => {
                  const start = todayTimes.fajr.getTime();
                  const end = todayTimes.isha.getTime();
                  const pct = Math.max(0, Math.min(100, ((now.getTime() - start) / (end - start)) * 100));
                  return <div className="h-full bg-accent transition-all" style={{ width: `${pct}%` }} />;
                })()}
              </div>
            </div>
          )}
        </motion.div>

        {/* Qibla quick link */}
        <Link
          to="/$slug" params={{ slug: "qibla-direction" }}
          className="flex items-center gap-3 rounded-xl border border-border bg-card p-3 text-sm transition hover:border-accent"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent/10 text-accent"><Compass size={18} /></span>
          <span className="flex-1">
            <span className="font-semibold">{t(STR.qibla)}</span>
            <span className="ml-2 text-xs text-muted-foreground">
              {lang === "bn" ? "মক্কার দিকে দিকনির্দেশ →" : "Bearing to Makkah →"}
            </span>
          </span>
        </Link>

        {/* Monthly */}
        {showMonth && <MonthlyCalendar lang={lang} location={location} method={method} madhab={madhab} timeFormat={timeFormat} date={date} />}

        {/* Educational content */}
        <article className="prose prose-sm dark:prose-invert max-w-none space-y-2 text-sm">
          <h2 className="text-lg font-bold">{lang === "bn" ? "নামাজের সময় সম্পর্কে" : "About prayer times"}</h2>
          <p>
            {lang === "bn"
              ? "ইসলামের পাঁচ ওয়াক্ত নামাজ — ফজর, যোহর, আসর, মাগরিব, এশা — সূর্যের অবস্থানের ভিত্তিতে নির্ধারিত হয়। প্রতিটি ওয়াক্তের সঠিক সময় জানতে অক্ষাংশ-দ্রাঘিমা ও সঠিক কোণ-নির্ভর গণনা পদ্ধতি প্রয়োজন।"
              : "The 5 daily prayers — Fajr, Dhuhr, Asr, Maghrib, Isha — are timed by the sun's position. Accurate computation requires latitude/longitude and an angle-based calculation method."}
          </p>
          <h3 className="text-base font-semibold">{lang === "bn" ? "করাচী মেথড — বাংলাদেশের মান" : "Karachi method — BD standard"}</h3>
          <p>
            {lang === "bn"
              ? "করাচী মেথড (University of Islamic Sciences) বাংলাদেশের ইসলামিক ফাউন্ডেশন ও অধিকাংশ মাদ্রাসা অনুসরণ করে। ফজর ১৮° ও এশা ১৮° দিগন্তের নিচে।"
              : "The Karachi method (University of Islamic Sciences) is followed by Bangladesh's Islamic Foundation and most madrasas. Fajr 18°, Isha 18° below the horizon."}
          </p>
          <h3 className="text-base font-semibold">{lang === "bn" ? "মাযহাব অনুযায়ী আসর" : "Asr by madhhab"}</h3>
          <p>
            {lang === "bn"
              ? "হানাফী: ছায়া বস্তুর দ্বিগুণ + যোহরের ছায়া। শাফি'ঈ/মালিকী/হাম্বলী: ছায়া বস্তুর সমান + যোহরের ছায়া। তাই হানাফী আসর প্রায় ১ ঘণ্টা পরে।"
              : "Hanafi: shadow = 2× object + noon shadow. Standard: shadow = 1× object + noon shadow. Hanafi Asr is therefore ~1 hour later."}
          </p>
          <h3 className="text-base font-semibold">{lang === "bn" ? "মুসাফিরের নামাজ (কসর)" : "Traveler's prayer (qasr)"}</h3>
          <p>
            {lang === "bn"
              ? "প্রায় ৭৮ কিমি বা তার বেশি দূরত্বের সফরে যোহর, আসর, এশা ৪ রাকাতের পরিবর্তে ২ রাকাত পড়া যায়।"
              : "On a journey of ~78 km or more, Dhuhr, Asr, Isha may be shortened from 4 to 2 raka'at."}
          </p>
        </article>
      </div>
    </CalculatorLayout>
  );
}

function MonthlyCalendar({
  lang, location, method, madhab, timeFormat, date,
}: {
  lang: "en" | "bn";
  location: LocationState;
  method: MethodId;
  madhab: MadhabId;
  timeFormat: "12" | "24";
  date: Date;
}) {
  const year = date.getFullYear();
  const month = date.getMonth();
  const days = new Date(year, month + 1, 0).getDate();

  const rows = useMemo(() => {
    const out: { d: number; t: DailyTimes }[] = [];
    for (let i = 1; i <= days; i++) {
      const dt = new Date(year, month, i);
      out.push({ d: i, t: computePrayerTimes(location.lat, location.lon, dt, method, madhab) });
    }
    return out;
  }, [year, month, days, location.lat, location.lon, method, madhab]);

  const fmt = (d: Date) => {
    const s = formatTime(d, timeFormat);
    return lang === "bn" ? toBengaliNumerals(s) : s;
  };

  const monthName = date.toLocaleDateString(lang === "bn" ? "bn-BD" : "en-GB", { month: "long", year: "numeric" });

  return (
    <section className="overflow-x-auto rounded-xl border border-border bg-card">
      <p className="border-b border-border p-3 text-sm font-semibold">{monthName} · {lang === "bn" ? location.cityLabel.bn : location.cityLabel.en}</p>
      <table className="w-full min-w-[640px] text-xs">
        <thead className="bg-muted/40 text-muted-foreground">
          <tr>
            <th className="p-2 text-left">{lang === "bn" ? "তারিখ" : "Date"}</th>
            {(["fajr", "sunrise", "dhuhr", "asr", "maghrib", "isha"] as PrayerKey[]).map((k) => (
              <th key={k} className="p-2 text-left">{lang === "bn" ? PRAYER_LABELS[k].bn : PRAYER_LABELS[k].en}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border/60">
          {rows.map((r) => (
            <tr key={r.d}>
              <td className="p-2 font-semibold">{lang === "bn" ? toBengaliNumerals(r.d) : r.d}</td>
              <td className="p-2 tabular-nums">{fmt(r.t.fajr)}</td>
              <td className="p-2 tabular-nums">{fmt(r.t.sunrise)}</td>
              <td className="p-2 tabular-nums">{fmt(r.t.dhuhr)}</td>
              <td className="p-2 tabular-nums">{fmt(r.t.asr)}</td>
              <td className="p-2 tabular-nums">{fmt(r.t.maghrib)}</td>
              <td className="p-2 tabular-nums">{fmt(r.t.isha)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
